import React, { FunctionComponent, useEffect, useState, MouseEvent, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { equals, filter, map, pipe } from 'ramda';
import { IAppState } from '../../../../common/models/store/IAppState';
import {
  fetchUsersRoutine,
  changeUserRoleRoutine,
  changeUserStatusRoutine,
  changeUsersRoleRoutine,
  changeUsersTeamRoutine,
  resendInviteRoutine,
  changeUsersStatusRoutine
} from '../../routines';
import { IUserInfo } from '../../../../common/models/user/IUserInfo';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IUserSettingsFilter } from '../../../../common/models/filter/IUserSettingsFilter';
import SearchInput from '../../../../components/SearchInput';
import RegularButton from '../../../../components/RegularButton';
import Table from '../../../../components/Table';
import { getColumns } from './utils';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import { IUserRole } from '../../../../common/models/user/IUserRole';
import { IUserStatus } from '../../../../common/models/user/IUserStatus';
import { IUpdateRoleData } from '../../../../common/models/user/IUpdateRoleData';
import CustomPopover from '../../../../components/CustomPopover';
import TeamList from '../../../../containers/TeamList';
import RoleList from '../../../../components/RoleList';
import { IUpdateTeamData } from '../../../../common/models/user/IUpdateTeamData';
import { IPopoverChild } from '../../../../common/models/common/IPopoverChild';
import ModalWindow from '../../../../components/ModalWindow';
import CreateUserForm from '../CreateUserForm';
import { usePrevious } from '../../../../common/helpers/customHooks';
import { ISaveUserError } from '../../../../common/models/user/ISaveUserError';
import { User } from '../../../../common/models/user/User';
import { ISettingsUsersCounters } from '../../../../common/models/settings/ISettingsUsersCounters';
import RadioFilterItem from '../../../../components/RadioFilterItem';
import { DashboardColor } from '../../../../common/enums/DashboardColor';
import Icon from '../../../../components/Icon';
import { FormattedMessage } from 'react-intl';
import Illustration from '../../../../components/Illustration';
import { RoleType } from '../../../../common/enums/RoleType';
import { IUpdateStatusData } from '../../../../common/models/user/IUpdateStatusData';
import TeamFilter from '../../components/TeamFilter';
import styles from './styles.module.scss';

interface IProps {
  counters: ISettingsUsersCounters;
  loading: boolean;
  users: IUserInfo[] | null;
  isSaving: boolean;
  saveError: ISaveUserError | null;
  userId: string;
  fetchUsers: IBindingCallback1<IUserSettingsFilter>;
  changeUserRole: IBindingCallback1<IUserRole>;
  changeUserStatus: IBindingCallback1<IUserStatus>;
  changeUsersTeam: IBindingCallback1<IUpdateTeamData>;
  changeUsersRole: IBindingCallback1<IUpdateRoleData>;
  changeUsersStatus: IBindingCallback1<IUpdateStatusData>;
  resendInvite: IBindingCallback1<string>;
}

const UsersPane: FunctionComponent<IProps> = ({
  counters,
  users,
  userId,
  loading,
  isSaving,
  saveError,
  fetchUsers,
  changeUserRole,
  changeUsersRole,
  changeUsersTeam,
  changeUserStatus,
  changeUsersStatus,
  resendInvite
}) => {
  const defaultFilters: IUserSettingsFilter = {
    from: 0,
    to: 10,
    filter: '',
    sort: [{ id: 'name', desc: false }],
    inactive: false
  };

  const [filters, setFilter] = useState(defaultFilters);
  const [selected, setSelected] = useState<IUserInfo[]>([]);
  const [isCreateShown, setIsCreateShown] = useState(false);

  const retrieveSelectedIds = (selectedUsers: IUserInfo[]) => map(({ id }) => id, selectedUsers);
  const selectedIds = retrieveSelectedIds(selected);
  const filteredSelectedIds = pipe<IUserInfo[], IUserInfo[], string[]>(
    filter(({ id, role }: IUserInfo) => id !== userId && role !== RoleType.SuperAdmin),
    retrieveSelectedIds
  )(selected);
  const bulkActionsDisabled = !filteredSelectedIds.length;

  useEffect(() => {
    fetchUsers(filters);
  }, [filters]);

  useEffect(() => {
    setSelected([]);
  }, [filters.inactive]);

  useEffect(() => {
    if (!users) {
      fetchUsers(filters);
    }
  }, [users]);

  const updateFilter = (newFilter: Partial<IUserSettingsFilter>) => {
    setFilter(state => {
      const updatedFilter = { ...state, ...newFilter };
      return equals(updatedFilter, state) ? state : updatedFilter;
    });
  };

  const loadData = (from: number, to: number) => {
    fetchUsers({ ...filters, from, to });
  };

  const onCellClick = ({ accessor }: IRowAccessor<IUserInfo>, event: MouseEvent<HTMLElement>) => {
    switch (accessor) {
      case 'role':
      case 'team':
      case 'isActive':
        event.stopPropagation();
        break;
      default:
        break;
    }
  };

  const RoleListComponent = ({ hidePopover }: IPopoverChild) => (
    <RoleList
      onSelect={role => {
        if (hidePopover) hidePopover();
        changeUsersRole({ userIds: filter(id => id !== userId, filteredSelectedIds), role });
      }}
    />
  );

  const TeamListComponent = useCallback(({ hidePopover }: IPopoverChild) => (
    <TeamList
      onSelect={([team]) => {
        if (hidePopover) hidePopover();
        changeUsersTeam({ userIds: selectedIds, teamId: team.id });
      }}
    />
  ), [selected]);

  const prevIsSaving = usePrevious(isSaving);

  useEffect(() => {
    if (!isSaving && prevIsSaving && !saveError) {
      setIsCreateShown(false);
      setFilter(defaultFilters);
    }
  }, [isSaving]);

  const noDataPlaceholder = (
    <Illustration
      variant="binoculars"
      height="22rem"
      header={<FormattedMessage id="resultsNotFound" defaultMessage="Hmm... no matches found" />}
      description={(
        <FormattedMessage
          id="resultsNotFoundHint"
          defaultMessage="Try searching for something else"
        />
      )}
    />
  );

  const columns = useMemo(
    () => getColumns(filters.filter, userId, changeUserRole, changeUserStatus, resendInvite),
    [filters.filter]
  );

  const total = filters.inactive ? counters.inactive : counters.active;

  return (
    <div className={styles.usersPane}>
      <ModalWindow
        contentClassName={styles.createModalContent}
        isShown={isCreateShown}
        onHide={() => setIsCreateShown(false)}
      >
        <CreateUserForm />
      </ModalWindow>
      <header>
        <div className="d-flex align-items-center mb-4">
          <RadioFilterItem
            id="activeUsers"
            name="activeUsers"
            count={counters.active}
            color={DashboardColor.Neutral}
            intlId="usersPane.activeUsers"
            defaultText="Active users"
            isSelected={!filters.inactive}
            onSelect={() => updateFilter({ inactive: false })}
          />
          <RadioFilterItem
            id="deactivatedUsers"
            name="deactivatedUsers"
            count={counters.inactive}
            color={DashboardColor.Neutral}
            intlId="usersPane.deactivatedUsers"
            defaultText="Deactivated users"
            isSelected={filters.inactive}
            onSelect={() => updateFilter({ inactive: true })}
          />
        </div>
        <div className="d-flex align-items-end mb-4">
          <TeamFilter onChange={teamIds => updateFilter({ teamIds })} selectedTeamIds={filters.teamIds} />
          <SearchInput
            value={filters.filter}
            onChange={val => updateFilter({ filter: val })}
            intlId="usersPane.search"
            defaultPlaceholder="Search for users"
          />
          <div className={styles.headerBttns}>
            {Boolean(selected.length) && (
              <>
                {!filters.inactive && (
                  <>
                    <CustomPopover
                      rootClose
                      trigger={(
                        <RegularButton
                          variant="tertiary"
                          textIntlId="usersPane.changeRole"
                          defaultMessage="Change role"
                          icon={<Icon icon="changeRole" />}
                          disabled={bulkActionsDisabled}
                          enableAction
                          onClick={event => (bulkActionsDisabled ? event.stopPropagation() : null)}
                        />
                      )}
                    >
                      <RoleListComponent />
                    </CustomPopover>
                    <CustomPopover
                      rootClose
                      trigger={(
                        <RegularButton
                          variant="tertiary"
                          textIntlId="usersPane.changeTeam"
                          defaultMessage="Change team"
                          icon={<Icon icon="userChange" />}
                        />
                      )}
                    >
                      <TeamListComponent />
                    </CustomPopover>
                  </>
                )}
                <RegularButton
                  variant="tertiary"
                  textIntlId={`usersPane.${filters.inactive ? 'reactivate' : 'deactivate'}`}
                  defaultMessage={filters.inactive ? 'Reactivate' : 'Deactivate'}
                  icon={<Icon icon={filters.inactive ? 'unarchive' : 'archive'} />}
                  disabled={bulkActionsDisabled}
                  onClick={() => changeUsersStatus({ userIds: filteredSelectedIds, isActive: filters.inactive })}
                />
              </>
            )}
            <RegularButton
              icon={<Icon icon="plus" />}
              variant="secondary"
              textIntlId="usersPane.addUser"
              defaultMessage="Add User"
              onClick={() => setIsCreateShown(true)}
            />
          </div>
        </div>
      </header>
      <Table
        config={{
          columns,
          data: { items: users || [], total: loading ? 0 : total },
          onLoad: loadData,
          loading,
          onFilterUpdated: ({ sort }) => updateFilter({ sort }),
          sortingEnabled: true,
          rowSelection: 'multiple',
          onCellClicked: onCellClick,
          onSelectionChanged: setSelected,
          noDataPlaceholder,
          defaultSorted: filters.sort
        }}
      />
    </div>
  );
};

const mapStateToProps = ({
  settings: {
    usersAndTeams: {
      users, loading, counters, isSaving, saveError
    }
  },
  user: { user }
}: IAppState) => ({ users, loading, counters, isSaving, saveError, userId: (user as User).id });

const mapDispachToProps = {
  fetchUsers: fetchUsersRoutine,
  changeUserRole: changeUserRoleRoutine,
  changeUserStatus: changeUserStatusRoutine,
  changeUsersRole: changeUsersRoleRoutine,
  changeUsersTeam: changeUsersTeamRoutine,
  changeUsersStatus: changeUsersStatusRoutine,
  resendInvite: resendInviteRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(UsersPane);
